import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { StackedMessagesService } from '@shared/stacked-messages/stacked-messages.service';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';

import { UserAssetsHttpService } from '@core/http/user-assets.http.service';
import * as actions from './decision-documents.actions';

@Injectable()
export class DecisionDocumentsEffects {
  private readonly actions$ = inject(Actions);
  private readonly stackedMessages = inject(StackedMessagesService);
  private readonly userAssetsService = inject(UserAssetsHttpService);

  loadDecisionDocuments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadDecisionDocuments),
      switchMap(({ userProcessId, asConsultant, errorMessageKey }) => {
        const documents$ = asConsultant
          ? this.userAssetsService.getDecisionDocumentsAsConsultant({ userProcessId })
          : this.userAssetsService.getDecisionDocuments({ userProcessId });
        return documents$.pipe(
          map(documents => actions.loadDecisionDocumentsSuccess({ documents: documents || [] })),
          catchError(() => {
            this.stackedMessages.showError({
              message: errorMessageKey || 'MANAGEMENT.DECISION_MODAL.DOCUMENTS.ERROR_LOADING',
            });
            return of(actions.loadDecisionDocumentsError());
          })
        );
      })
    )
  );

  removeDecisionDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.removeDecisionDocument),
      mergeMap(
        ({ userProcessId, userAsset, asConsultant, successMessageKey, errorMessageKey, reloadErrorMessageKey }) =>
          this.userAssetsService.removeMyAsset({ userAssetId: userAsset.id }).pipe(
            map(() => {
              this.stackedMessages.showSuccess({
                message: successMessageKey || 'MANAGEMENT.DECISION_MODAL.DOCUMENTS.REMOVE_SUCCESS',
                translateParams: { fileName: userAsset.fileOriginalName },
              });
              return actions.removeDecisionDocumentSuccess({
                userProcessId,
                userAssetId: userAsset.id,
                fileOriginalName: userAsset.fileOriginalName,
                asConsultant,
                reloadErrorMessageKey,
              });
            }),
            catchError(() => {
              this.stackedMessages.showError({
                message: errorMessageKey || 'MANAGEMENT.DECISION_MODAL.DOCUMENTS.ERROR_REMOVE',
                translateParams: { fileName: userAsset.fileOriginalName },
              });
              return of(actions.removeDecisionDocumentError({ userAssetId: userAsset.id }));
            })
          )
      )
    )
  );

  reloadAfterRemove$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.removeDecisionDocumentSuccess),
      map(({ userProcessId, asConsultant, reloadErrorMessageKey }) =>
        actions.loadDecisionDocuments({ userProcessId, asConsultant, errorMessageKey: reloadErrorMessageKey })
      )
    )
  );
}
